<template>
	<v-container>
		<v-form
			ref="checkoutDeviceForm"
		>
			<v-row>
				<v-col class="mb-0 pb-0">
					<h3>
						<translate>Your device</translate>
					</h3>
				</v-col>
			</v-row>
			<v-row
				class="d-flex"
			>
				<v-col class="mt-0 pt-0 flex-grow-1 flex-shrink-0">
					<h4>
						<translate>
							{{ device.name }}
						</translate>
					</h4>
				</v-col>
				<v-col
					class="mt-0 pt-0 flex-shrink-1 flex-grow-0"
				>
					<v-btn
						href="/"
						color="primary"
					>
						<translate>Change device</translate>
					</v-btn>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="d-flex align-center mb-0 pb-0">
					<v-text-field
						v-model:model-value="device.imei"
						:label="$gettext('IMEI')"
						:rules="[v => !!v || $gettext('IMEI is required'),
							v => /^\d{15}$/.test(v) || $gettext('A valid IMEI of 15 digits is required')]"
						validate-on="blur"
						variant="outlined"
						required
						hide-details="auto"
					>
						<template
							v-if="device.imei && device.imei.length > 0 && /^\d{15}$/.test(device.imei)"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="align-center mt-0 pt-0">
					<imei-info-dialog />
				</v-col>
			</v-row>
			<v-row>
				<v-col class="d-flex align-center mb-0 pb-0">
					<v-text-field
						v-model:model-value="device.ean"
						:label="$gettext('EAN')"
						:rules="[v => !!v || $gettext('EAN is required'),
							v => /^\d{13}$/.test(v) || $gettext('A valid EAN of 13 digits is required')]"
						validate-on="blur"
						variant="outlined"
						required
						hide-details="auto"
					>
						<template
							v-if="device.ean && device.ean.length > 0 && /^\d{13}$/.test(device.ean)"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="align-center mt-0 pt-0">
					<ean-info-dialog />
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<date-text-field
						v-model:model-value="device.purchaseDate"
						append-on-valid="$check"
						:label="$gettext('Purchase Date')"
						required-message="Purchase date is required"
						prepend-inner-icon="$calendar"
						variant="outlined"
						persistent-placeholder
						validate-on="blur"
						hide-details="auto"
						clearable
						required
						:custom-validator="validateDate"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="d-flex align-center mb-0 pb-0">
					<price-field
						v-model:model-value="device.purchasePrice"
						:label="$gettext('Purchase price')"
						:rules="[v => !!v || $gettext('Purchase Price is required')]"
						required
						validate-on="blur"
						show-checkmark
						hide-details="auto"
						variant="outlined"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="align-center mt-0 pt-0">
					<purchase-price-info-dialog />
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-text-field
						v-model:model-value="device.purchasedAt"
						:label="$gettext('Purchased at which (online) store')"
						variant="outlined"
						placeholder="Belsimpel"
						persistent-placeholder
						validate-on="blur"
						hide-details="auto"
					>
						<template
							v-if="device.purchasedAt && device.purchasedAt.length > 0"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-text-field
						v-model:model-value="device.iban"
						label="IBAN"
						variant="outlined"
						required
						:rules="[v => !!v || $gettext('IBAN is required'),
							v => ibanRegex.test(v) || $gettext('A valid IBAN is required')]"
						validate-on="blur"
						hide-details="auto"
					>
						<template
							v-if="device.iban && device.iban.length > 0"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-checkbox
						v-model:model-value="device.policyAuthorization"
						color="primary"
					>
						<template
							#label
						>
							<p
								style="margin-bottom: 0px"
							>
								<translate>
									I authorize Tulip Assist to automatically collect the premium from the above account number every month.
								</translate>
								<a
									href="/klantenservice/polisvoorwaarden"
									@click.stop
								>
									<span>
										<translate>I agree to the policy conditions</translate>
									</span>
								</a>
							</p>
						</template>
					</v-checkbox>
				</v-col>
			</v-row>
		</v-form>
	</v-container>
</template>

<script>
import DateTextField from '../atoms/DateTextField.vue';
import PriceField from '../atoms/PriceField.vue';
import ImeiInfoDialog from './ImeiInfoDialog.vue';
import EanInfoDialog from './EanInfoDialog.vue';
import PurchasePriceInfoDialog from './PurchasePriceInfoDialog.vue';

export default {
	name: 'CheckoutDevice',
	components: {
		PurchasePriceInfoDialog,
		EanInfoDialog,
		ImeiInfoDialog,
		DateTextField,
		PriceField,
	},
	props: {
		modelValue: {
			type: Object,
			default: null
		}
	},

	emits: ['update:modelValue',],

	data () {
		return {
			device: {
				...this.modelValue
			},
			ibanRegex: /^(?:(?:IT|SM)\d{2}[A-Z]\d{22}|CY\d{2}[A-Z]\d{23}|NL\d{2}[A-Z]{4}\d{10}|LV\d{2}[A-Z]{4}\d{13}|(?:BG|BH|GB|IE)\d{2}[A-Z]{4}\d{14}|GI\d{2}[A-Z]{4}\d{15}|RO\d{2}[A-Z]{4}\d{16}|KW\d{2}[A-Z]{4}\d{22}|MT\d{2}[A-Z]{4}\d{23}|NO\d{13}|(?:DK|FI|GL|FO)\d{16}|MK\d{17}|(?:AT|EE|KZ|LU|XK)\d{18}|(?:BA|HR|LI|CH|CR)\d{19}|(?:GE|DE|LT|ME|RS)\d{20}|IL\d{21}|(?:AD|CZ|ES|MD|SA)\d{22}|PT\d{23}|(?:BE|IS)\d{24}|(?:FR|MR|MC)\d{25}|(?:AL|DO|LB|PL)\d{26}|(?:AZ|HU)\d{27}|(?:GR|MU)\d{28})$/
		};
	},

	watch: {
		modelValue: {
			/**
			 * Watch the value passed to the component and set if changed.
			 */
			handler () {
				this.device = this.modelValue;
			},
			deep: true
		},

		device: {
			/**
			 * Watch if any changes are made in the form and emit to parent
			 */
			handler () {
				this.$emit('update:modelValue', this.device);
			},
			deep: true
		}
	},

	methods: {
		/**
		 * Validate the form
		 *
		 * @returns {*}
		 */
		async validateInformation () {
			const {
				valid
			} = await this.$refs.checkoutDeviceForm.validate();

			return valid;
		},

		/**
		 * Ensure the entered date is not in the future or older than 3 months ago
		 *
		 * @param {string} value
		 * @returns {boolean}
		 */
		validateDate (value) {
			const now = new Date();
			const threeMonths = new Date();
			const date = new Date(value);

			now.setHours(0, 0, 0, 0);
			threeMonths.setHours(0, 0, 0, 0);
			date.setHours(0, 0, 0, 0);
			threeMonths.setMonth((threeMonths.getMonth() - 3));
			return (((date <= now) && (date >= threeMonths)) || this.$gettext('You can only insure devices that have been purchased no more than three months ago'));
		}
	}
};
</script>
